import { useEffect, useRef, useState, useCallback } from "react";
import AppBar from "@/components/AppBar";
import { useSearchParams, Link } from "react-router-dom";
import { api, asArray } from "@/lib/api";
import MasterSearch from "@/components/MasterSearch";
import VenueCard, { VenueCardSkeleton, SmartImg } from "@/components/VenueCard";
import { useCity } from "@/context/CityContext";
import { useTick } from "@/context/LiveContext";
import { CATEGORIES as CATS, CAT, catLabel, isService, isFood } from "@/lib/categories";
import { LayoutGrid, Sparkles, Loader2 } from "lucide-react";

const CATEGORIES = [["", "All", LayoutGrid], ...CATS.map((c) => [c.key, c.plural, c.Icon])];
const PAGE = 9;

export default function Properties() {
  const [sp] = useSearchParams();
  const { city: ctxCity, setCity } = useCity();
  const [venues, setVenues] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [cat, setCat] = useState(sp.get("category") || "");
  const [premium, setPremium] = useState([]);
  const sentinel = useRef(null);
  const tick = useTick();

  const city = sp.get("city") ?? ctxCity ?? "";
  const q = sp.get("q"), pax_min = sp.get("pax_min"), veg = sp.get("veg");

  useEffect(() => { if (sp.get("city")) setCity(sp.get("city")); }, [sp]);
  useEffect(() => { setCat(sp.get("category") || ""); }, [sp]);

  const params = { city: city || undefined, q, pax_min, veg, category: cat || undefined };

  useEffect(() => {
    setLoading(true); setPage(1);
    api.get("/venues/paged", { params: { ...params, page: 1, limit: PAGE } }).then((r)=>{ setVenues(asArray(r.data?.items)); setTotal(Number(r.data?.total) || 0); }).catch(()=>{ setVenues([]); setTotal(0); }).finally(()=>setLoading(false));
  }, [city, q, pax_min, veg, cat]); // eslint-disable-line react-hooks/exhaustive-deps

  // premium strip (refreshes on its own)
  useEffect(() => {
    const spotParams = { highlighted: true, limit: 30, city: city || undefined, category: cat || undefined };
    api.get("/venues", { params: spotParams }).then((r)=>setPremium(asArray(r.data).slice(0,10))).catch(()=>{});
  }, [city, cat, tick]);

  // quiet refresh of the first page while nobody has scrolled further
  useEffect(() => {
    if (!tick || page !== 1) return;
    api.get("/venues/paged", { params: { ...params, page: 1, limit: PAGE } }).then((r)=>{ setVenues(asArray(r.data?.items)); setTotal(Number(r.data?.total) || 0); }).catch(()=>{});
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(() => {
    if (more || loading || venues.length >= total) return;
    setMore(true);
    const next = page + 1;
    api.get("/venues/paged", { params: { ...params, page: next, limit: PAGE } }).then((r)=>{ setVenues((v)=>[...v, ...asArray(r.data?.items)]); setPage(next); }).catch(()=>{}).finally(()=>setMore(false));
  }, [more, loading, venues.length, total, page, city, q, pax_min, veg, cat]);

  useEffect(() => {
    const el = sentinel.current; if (!el) return;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) loadMore(); }, { rootMargin: "400px" });
    io.observe(el); return () => io.disconnect();
  }, [loadMore]);


  return (
    <div>
      <AppBar/>
      <section className="relative mesh-hero text-white" data-testid="properties-hero">
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-10 md:py-16">
          <h1 className="font-display text-[34px] leading-[1.1] sm:text-5xl md:text-6xl text-white">
            {cat ? CAT[cat]?.plural || catLabel(cat) : "Every listing"} <span className="text-[color:var(--gold-2)]">{city ? <>in {city}</> : "across India"}</span>
          </h1>
          <div className="mt-6"><MasterSearch defaultCity={city || ""} defaultCategory={cat}/></div>
        </div>
      </section>

      {/* Category chips */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 pt-8" data-testid="category-filters">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
          {CATEGORIES.map(([k, l, I])=>(
            <button key={k} onClick={()=>setCat(k)} className={`press shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[15px] font-semibold border transition-all ${cat===k ? "bg-[color:var(--maroon)] text-[color:var(--cream-primary)] border-[color:var(--maroon)] shadow-[0_10px_24px_rgba(106,32,35,0.3)]" : "bg-white border-[color:var(--gold)]/50 text-[color:var(--maroon)] hover:border-[color:var(--maroon)]"}`} data-testid={`filter-cat-${k||"all"}`}>
              <I className={`w-4 h-4 ${cat===k ? "text-[color:var(--gold-2)]" : "text-[color:var(--gold)]"}`}/> {l}
            </button>
          ))}
        </div>
      </section>

      {/* Premium spotlight */}
      {premium.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-8" data-testid="listing-premium">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[color:var(--gold)]"/>
            <div className="text-[15px] font-semibold text-[color:var(--maroon)]" data-testid="listing-premium-title">{cat ? `Premium ${CAT[cat]?.plural?.toLowerCase() || "listings"}` : "Premium spotlight"}{city ? ` · ${city}` : ""}</div>
          </div>
          <div className="overflow-hidden rounded-3xl border border-[color:var(--gold)]/40 bg-[color:var(--ivory-2)]/60 py-4">
            <div className="marquee px-4">
              {[...premium, ...premium].map((v, i)=>(
                <Link to={`/property/${v.id}`} key={i} className="min-w-[300px] max-w-[300px] card-luxury overflow-hidden block" data-testid={`listing-premium-item-${i}`}>
                  <div className="relative">
                    <SmartImg src={v.images?.[0]} category={v.category} className="h-44 w-full object-cover" alt={v.name}/>
                    <span className="absolute top-2 left-2 text-[12px] bg-[color:var(--gold-2)] text-[color:var(--maroon-2)] px-2 py-0.5 rounded-full font-bold">#{(i % premium.length)+1} · {catLabel(v.category)}</span>
                  </div>
                  <div className="p-4">
                    <div className="font-display text-xl leading-tight text-[color:var(--maroon)]">{v.name}</div>
                    <div className="text-[13px] text-neutral-600 mt-1">{v.city}{!isService(v.category) && v.pax_max ? ` · up to ${v.pax_max} guests` : ""}{isFood(v.category) ? " · catering" : ""}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-8">
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="venue-grid">
          {loading && Array.from({length:6}).map((_,i)=><VenueCardSkeleton key={i}/>)}
          {!loading && venues.length === 0 && (
            <div className="col-span-full text-center py-24 text-neutral-500 border border-dashed border-[color:var(--gold)]/50 rounded-3xl">No listings match. Try broadening your search.</div>
          )}
          {!loading && venues.map((v)=><VenueCard key={v.id} v={v}/>)}
        </div>
        <div ref={sentinel} className="h-10"/>
        {more && <div className="flex justify-center py-6 text-[color:var(--maroon)]" data-testid="loading-more"><Loader2 className="w-6 h-6 animate-spin"/></div>}
        {!loading && venues.length > 0 && venues.length >= total && <div className="text-center text-xs text-neutral-400 py-6">Showing all {total} listings</div>}
      </section>
    </div>
  );
}
